import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Briefcase, CreditCard, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { buildPublicInvoiceDocument } from '@/components/documents/adapters';
import { usePortal } from '../PortalContext';
import { usePortalInvoice } from '../hooks/usePortalInvoices';
import { usePortalJob } from '../hooks/usePortalJobs';
import { invoiceBalance } from '../hooks/portalClient';
import { toPublicInvoicePayload } from '../documents';
import { invoiceStatus } from '../status';
import { PortalStatusBadge } from '../components/PortalStatusBadge';
import { PortalDocumentView } from '../components/PortalDocumentView';
import { PortalLoading } from '../components/PortalLoading';
import { PortalErrorState } from '../components/PortalErrorState';

export default function PortalInvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const { slug, company, locale, timezone, formatMoney, formatDate, formatShortDate, primaryColor } = usePortal();
  const query = usePortalInvoice(company.id, id);
  const invoice = query.data ?? null;
  const jobQuery = usePortalJob(company.id, invoice?.job_id ?? undefined);
  const job = jobQuery.data ?? null;

  const doc = useMemo(() => {
    if (!invoice) return null;
    return buildPublicInvoiceDocument(toPublicInvoicePayload(invoice, company, job), locale, { timezone });
  }, [invoice, job, company, locale, timezone]);

  if (query.isLoading) return <PortalLoading label="Loading invoice…" />;
  if (query.error) return <PortalErrorState onRetry={() => void query.refetch()} />;
  if (!invoice || !doc) {
    return <PortalErrorState title="Invoice not found" message="This invoice isn't available on your account." />;
  }

  const status = invoiceStatus(invoice, formatShortDate);
  const balance = invoiceBalance(invoice);
  const paid = Number(invoice.amount_paid) || 0;
  const owing = balance > 0 && invoice.status !== 'cancelled' && invoice.status !== 'refunded';
  const companyName = company.trading_name || company.name;

  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" size="sm" className="-ml-2 gap-1.5">
        <Link to={`/portal/${slug}/home`}><ArrowLeft className="h-4 w-4" /> Back to your account</Link>
      </Button>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm text-muted-foreground">Invoice {invoice.invoice_number}</p>
              {owing ? (
                <>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Balance due</p>
                  <p className="text-3xl font-semibold tabular-nums">{formatMoney(balance)}</p>
                </>
              ) : (
                <p className="text-2xl font-semibold tabular-nums">{formatMoney(invoice.total_amount)}</p>
              )}
            </div>
            <PortalStatusBadge status={status} />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-4">
            <Fact label="Total" value={formatMoney(invoice.total_amount)} />
            {paid > 0 && <Fact label="Paid" value={formatMoney(paid)} />}
            {invoice.status === 'paid' && invoice.paid_date
              ? <Fact label="Paid on" value={formatDate(invoice.paid_date)} />
              : invoice.due_date && <Fact label="Due" value={formatDate(invoice.due_date)} />}
            {invoice.issue_date && <Fact label="Issued" value={formatDate(invoice.issue_date)} />}
          </div>

          {owing && (
            <>
              <Separator className="my-4" />
              {invoice.payment_url ? (
                <div className="space-y-2">
                  <Button asChild size="lg" className="w-full gap-2 sm:w-auto" style={{ backgroundColor: primaryColor }}>
                    <a href={invoice.payment_url} target="_blank" rel="noopener noreferrer">
                      <CreditCard className="h-4 w-4" /> Pay online by card
                    </a>
                  </Button>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Lock className="h-3 w-3" /> Secure payment by Square. Opens in a new tab.
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  To pay this invoice, contact {companyName}
                  {company.phone ? ` on ${company.phone}` : company.email ? ` at ${company.email}` : ''}.
                  {company.payment_instructions ? ' Payment details are shown on the invoice below.' : ''}
                </p>
              )}
            </>
          )}

          {invoice.job_id && job && (
            <>
              <Separator className="my-4" />
              <Link
                to={`/portal/${slug}/jobs/${invoice.job_id}`}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
              >
                <Briefcase className="h-4 w-4" /> View job {job.job_number}
              </Link>
            </>
          )}
        </CardContent>
      </Card>

      <PortalDocumentView doc={doc} />
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="font-medium tabular-nums">{value}</p>
    </div>
  );
}
